import React, { ReactElement } from 'react';
import styled, { CSSObject } from '@emotion/styled';
/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { getBackgroundTheme, getColor, getTextColor, type } from '../../style';
import { isDarkMode, IsMobileViewPort, tabletWidth } from '../../utils/helpers';
import PriceInput, { adjustStylePerDigit } from '../../components/pages/Offers/PriceInput';
import OfferPriceBtnSet from '../../components/pages/Offers/OfferPriceBtnSet';
import ButtonSet from '../../components/blocks/ButtonSet';
import Button from '../buttons/Button';
import WYPButtonIcon from '../icons/WYPButtonIcon';
import useModal from '../../hooks/useModal';
import { sendCancel } from '../../actions/profileTrackAction';
import { tsSince } from '../../common';
import { CurrencyLabelSpan, getInputProps } from '../../components/pages/Offers/common';
import { useDispatch, useSelector } from 'react-redux';
import { ignoreOffer } from '../../actions/offerActions';
import { refreshExternalProfile } from '../../actions/profileActions';
import { CustomStyledProps } from 'style/style';
import SkeletonLoader from '../SkeletonLoader';
import breakpoints from '../../theme/material/breakpoints';
import { RootState } from '../../store';
import { FONT_FAMILIES } from 'theme/material/constants';

const PriceContainer = styled('div')(
  {
    position: 'relative',
    marginLeft: 'auto',
    marginRight: 'auto',
    background: 'red',
    textAlign: 'center',
    width: '100%',
    borderRadius: 4,
    boxShadow: 'inset 2px 2px 4px rgba(0, 0, 0, 0.12)',
    fontFamily: FONT_FAMILIES.secondary,
    [breakpoints.down(639.05)]: {
      width: '95%',
    },
  },
  ({ hasErrors }: CustomStyledProps) => ({
    backgroundColor: hasErrors ? 'rgba(204, 84, 84,0.15)' : getBackgroundTheme().block2,
    border: hasErrors ? '1px solid #cc5454' : isDarkMode() ? '1px solid #212121' : '1px solid #C4C4C4',
  })
);

const CurrencyTip = styled('div')(
  {
    display: 'inline-block',
    position: 'relative',
    lineHeight: 'normal',
    fontFamily: FONT_FAMILIES.secondary,
  },
  ({ offerPrice }: CustomStyledProps) => ({
    ...setColor()[offerPrice],
    ':before': {
      position: 'absolute',
      top: 14,
      right: '85%',
      fontSize: 16,
    },
  })
);

const Time = styled('div')(
  (): CSSObject => ({
    color: '#8492a6',
    textAlign: 'center',
    ...type.SM,
    position: 'absolute',
    bottom: -10,
    right: -45,
    [breakpoints.down(620.05)]: {
      bottom: -15,
      right: 10,
    },
  })
);

const ButtonDiv = styled('div')({
  paddingTop: 0,
  marginTop: 10,
  [breakpoints.down(639.05)]: {
    paddingTop: 0,
    marginTop: 10,
    width: '100%',
  },
});

const OfferAmounts = [50, 100, 150, 200, '???'];

// @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
const DeclineButton = styled(Button)({
  borderColor: '#2490de',
  color: '#2490de',
  backgroundColor: '#fff',

  '&:hover': {
    color: '#fff',
    backgroundColor: '#2490de',
  },
});

const MaxOfferText = styled('p')({
  margin: '0px auto',
  textAlign: 'center',
  ...type.XS,
  lineHeight: '16px',
  letterSpacing: -0.3,
  ...getTextColor().DARK_2,
});

const CustomButtonStyles = {
  fontFamily: FONT_FAMILIES.primary,
  borderRadius: '4px !important',
  padding: '11px 24px',
  lineHeight: '16px',
  letterSpacing: -0.32,
  '&:hover': {
    opacity: 0.7,
  },
};

const skeletonStyle = {
  position: 'absolute',
  zIndex: 1,
  height: '100%',
  width: '100%',
  padding: 0,
  '& > svg ': {
    display: 'inherit',
    width: `100%`,
    height: '100%',
  },
};

const setColor = () => ({
  default: {
    color: 'grey',
  },
  brand: {
    color: getColor().BRAND_1,
  },
  alert: { ...getTextColor().RED },
});

const status = currentUserIsGenerous => (currentUserIsGenerous ? 'Offer' : 'Request');

const colorType = (price, hasErrors) => {
  switch (price) {
    case true:
      return 'default';
    case false:
      return !hasErrors ? 'brand' : 'alert';
    default:
      return;
  }
};

interface InitialOfferPanelProps {
  hasErrors: any;
  inputRef: any;
  offerPrice: any;
  inputOnChange: any;
  validateTextInput: any;
  updateOfferPrice: any;
  currencyInitial: any;
  currLabel: any;
  currentUserIsGenerous: any;
  offer: any;
  createdAt: any;
  handleSubmit: any;
  sendOfferBtn: any;
  isOfferSending: boolean;
  otherAccount: any;
  isGenerous: any;
}

const InitialOfferPanel = ({
  hasErrors,
  offerPrice,
  inputRef,
  inputOnChange,
  validateTextInput,
  updateOfferPrice,
  currencyInitial,
  currLabel,
  currentUserIsGenerous,
  offer,
  createdAt,
  handleSubmit,
  sendOfferBtn,
  isOfferSending,
  otherAccount,
  isGenerous,
}: InitialOfferPanelProps): ReactElement => {
  const dispatch = useDispatch();
  const { showSkeleton } = useSelector((state: RootState) => state.search);
  const { confirmModal, resetModal } = useModal();

  const handleIgnoreOffer = hashId => {
    dispatch(ignoreOffer(hashId));
    setTimeout(() => dispatch(refreshExternalProfile(hashId)), 0);
  };

  const handleCancelSentOffer = () => {
    confirmModal({
      dataTestID: 'offer-cancel-sent-offers',
      modalContentLabel: 'Offers Decline Sent Offers',
      confirmationProps: {
        title: `Decline ${isGenerous ? 'Offer' : 'Request'}?`,
        message: `Are you sure you want to decline your ${
          isGenerous ? 'offer' : 'request'
        } to <strong>${otherAccount.username}</strong>?`,
        confirmText: `Decline ${isGenerous ? 'Offer' : 'Request'}`,
        confirmAction: async () => {
          await handleIgnoreOffer(otherAccount.hash_id);
          sendCancel();
          resetModal();
        },
      },
    });
  };

  const formatSubmitButtonText = () => {
    return isOfferSending ? `Sending ${status(currentUserIsGenerous)}...` : `Send ${status(currentUserIsGenerous)}`;
  };

  return (
    <div style={{ position: 'relative' }}>
      <PriceContainer hasErrors={hasErrors}>
        {showSkeleton && (
          // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
          <SkeletonLoader customStyle={skeletonStyle}>
            <rect x="0" y="0" rx="4" ry="4" width="100%" height="100%" />
          </SkeletonLoader>
        )}
        <CurrencyTip
          offerPrice={colorType(offerPrice, hasErrors)}
          style={{
            paddingLeft: adjustStylePerDigit(offerPrice).paddingLeft,
          }}
        >
          <CurrencyLabelSpan
            customCurrencyLabelStyle={{
              fontSize: 60,
              fontWeight: 400,
              lineHeight: '50px',
              letterSpacing: -0.3,
              [breakpoints.down('sm')]: { fontSize: '50px', lineHeight: '50px' },
            }}
          >
            {currencyInitial}
            {currLabel}
          </CurrencyLabelSpan>
          <PriceInput
            inputRef={inputRef}
            data-test-id="profile-offer-price-input"
            hasErrors={hasErrors}
            value={offerPrice}
            onChange={inputOnChange}
            validateTextFn={validateTextInput}
            minWidth={IsMobileViewPort && !tabletWidth ? 80 : 115}
            inputProps={{
              style: {
                ...getInputProps().style,
                marginLeft: adjustStylePerDigit(offerPrice).marginLeft,
                textAlign: 'left',
                fontSize: 60,
                fontWeight: 400,
                lineHeight: '50px',
                letterSpacing: -0.3,
              },
            }}
            responsiveFontSize={`
              @media (max-width: 1023.95px) {
                font-size: 50px !important;
                line-height: 50px !important;
              }
           `}
          />
        </CurrencyTip>
      </PriceContainer>
      <OfferPriceBtnSet
        prices={OfferAmounts}
        updateOfferPrice={updateOfferPrice}
        currentAmount={offerPrice}
        currLabel={currLabel}
        currInitial={currencyInitial}
        offer={offer}
        isCounter={false}
        customStyle={{ marginTop: 10, marginBottom: 10 }}
      />
      <MaxOfferText>
        Maximum offer amount is {currencyInitial}
        {currLabel}500
      </MaxOfferText>
      <ButtonDiv>
        <ButtonSet align="center">
          {offer && (
            <DeclineButton
              buttonType={'secondaryRed'}
              onClick={handleCancelSentOffer}
              customStyle={{ ...CustomButtonStyles, padding: '12px 24px' }}
            >
              Decline
            </DeclineButton>
          )}
          <Button
            id={'make-initial-offer'}
            buttonRef={sendOfferBtn}
            disabled={hasErrors || isOfferSending}
            buttonType="error"
            onClick={e => handleSubmit(e)}
            type="submit"
            customStyle={CustomButtonStyles}
          >
            <WYPButtonIcon style={{ marginRight: 6 }} />
            {formatSubmitButtonText()}
          </Button>
        </ButtonSet>
      </ButtonDiv>
      {createdAt ? <Time>{createdAt && tsSince(createdAt)}</Time> : null}
    </div>
  );
};

export default InitialOfferPanel;
